import {
  Box,
  Divider,
  Heading,
  ListItem,
  SimpleGrid,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import Section from "../primary/Section";
import BookletViewer from "./BookletViewer";
import { ServiceItem } from "./ServiceItem";
import { serviceSummaries } from "./ServiceSummaries";

const Services = () => {
  return (
    <Section id="pillars-expand" variant="white">
      <VStack align="center" textAlign="center" pt={{ base: 16, md: 20 }} pb={4}>
        <VStack
          id="cultural-exchange-project"
          spacing={3}
          w="full"
          alignSelf="flex-start"
          align="stretch"
          textAlign="left"
        >
          <Text
            fontSize="xs"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color="preview.primary"
            fontWeight={600}
          >
            Project
          </Text>
          <Heading
            as="h2"
            fontSize={{ base: "3xl", md: "5xl" }}
            color="preview.neutral"
          >
            Cultural Exchange
          </Heading>
          <VStack
            spacing={5}
            align="stretch"
            fontSize={{ base: "md", md: "lg" }}
            color="cle.muted"
            lineHeight="1.8"
          >
            <Text>
              Cultural Exchange is a community-led initiative developed in
              Galway to bring people from different cultural backgrounds
              together through storytelling, food, music, creativity, and
              shared lived experience.
            </Text>
            <Text>
              Each session is led and co-created by a volunteer who shares the
              beauty and meaning of their culture through their own perspective,
              moving beyond what is often shown in media and news.
            </Text>
            <Text>
              Through informal dialogue, creative activities, and shared
              cultural practices, Cultural Exchange supports deeper connections
              between people who may not otherwise meet. It helps participants
              recognise common experiences while also valuing difference,
              memory, identity, and place.
            </Text>
            <Text>
              At its heart, Cultural Exchange uses culture as a bridge to build
              empathy, intercultural understanding, friendship, and a stronger
              shared sense of home in Galway.
            </Text>
          </VStack>
        </VStack>
        <VStack spacing={4} pt={{ base: 10, md: 14 }}>
          <Text
            fontSize="xs"
            letterSpacing="0.2em"
            textTransform="uppercase"
            color="cle.muted"
            fontWeight={600}
          >
            What we are exploring
          </Text>
          <Heading
            as="h2"
            fontSize={{ base: "3xl", md: "4xl" }}
            color="preview.neutral"
          >
            Current learning
          </Heading>
        </VStack>
      </VStack>
      <SimpleGrid
        columns={[1, 1, 3]}
        columnGap={8}
        rowGap={10}
        margin="0 auto"
        padding={{ base: "32px 0 64px", md: "48px 0 80px" }}
      >
        {serviceSummaries.map((service) => (
          <ServiceItem key={service.slug} {...service} />
        ))}
      </SimpleGrid>
      <Divider
        borderColor="rgba(62, 146, 111, 0.18)"
        mb={{ base: 12, md: 16 }}
      />
      <VStack
        id="climate-stories-project"
        spacing={3}
        w="full"
        align="stretch"
        textAlign="left"
        pb={{ base: 16, md: 20 }}
      >
        <Text
          fontSize="xs"
          letterSpacing="0.14em"
          textTransform="uppercase"
          color="preview.primary"
          fontWeight={600}
        >
          Project
        </Text>
        <Heading
          as="h2"
          fontSize={{ base: "3xl", md: "5xl" }}
          color="preview.neutral"
        >
          Cultural Exchange for Climate Stories
        </Heading>
        <VStack
          spacing={5}
          align="stretch"
          fontSize={{ base: "md", md: "lg" }}
          color="cle.muted"
          lineHeight="1.8"
        >
          <Text>
            In response to Galway City Council’s invitation to take part in the
            Climate Inspiration Festival 2026, the Cultural Exchange programme
            was adapted to engage with climate change through culturally
            grounded storytelling and creative expression. The project was
            funded by Galway City Council through the Creative Communities Fund,
            an initiative of the Creative Ireland Programme.
          </Text>
          <Text>
            Cultural Exchange for Climate Stories brought people from different
            cultural backgrounds together to explore how climate change is
            experienced, remembered, and understood across different countries
            and cultures. One question shaped every stand: How has climate change
            touched my own life, my culture, and the places that have shaped who
            I am?
          </Text>
          <Text>
            Rather than presenting climate change only through facts or abstract
            information, the project created space for personal stories connected
            to land, water, food, seasons, storms, migration, resilience, and
            hope.
          </Text>
          <Text>
            Across three creative workshops with local artists, volunteers from
            five countries co-created country stands. The materials were mostly
            recycled. The stands shared climate stories through visual design,
            cultural objects, images, written reflections, and traditional snacks
            as an entry point for conversation with participants.
          </Text>
          <Box>
            <Text mb={2}>The exhibition was first presented at:</Text>
            <UnorderedList pl={5} spacing={1}>
              <ListItem>Climate Inspiration Festival 2026</ListItem>
              <ListItem>Westside Arts Festival</ListItem>
              <ListItem>Westside Public Library for three weeks</ListItem>
            </UnorderedList>
          </Box>
          <Text>
            By connecting climate action with culture and community
            participation, Cultural Exchange for Climate Stories made climate
            conversations more human, inclusive, and meaningful. It showed how
            storytelling and creativity can build empathy, deepen understanding,
            and bring people together around a scientific topic and a shared
            responsibility for the future.
          </Text>
          <Text>
            If you would like to know more about the project, you can read{" "}
            <BookletViewer />. The booklet tells the story of how the project came
            about, seen through the eyes of its designer, and then hands over to
            the volunteers and artists to describe it in their own words.
          </Text>
        </VStack>
      </VStack>
    </Section>
  );
};

export default Services;
